/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';
import {
  fetchProjectOptions,
  fetchActivityOptions,
  updateLoggedEffort,
  getEffortLoggingDates,
} from '../../../Api/request/timeSheetReport';
import DateRangeInput from '../../../Components/DateRangeInput/DateRangeInput';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import ActionConfirmModal from './ActionConfirmModal';
import './editEffortModal.scss';

dayjs.extend(customParseFormat);

const DEFAULT_FORM = {
  date: '',
  dateObj: null,
  project: '',
  effortHours: '',
  activity: '',
  comments: '',
};

const MONTH_INDEX = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

const getValueByPath = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj);


const validateUserInput = (data) => {
  // 1. Clean and normalize the input string
  const rawHours = String(data.efforts || '').trim();

  // 2. Ensure it's a valid positive number
  const isNumeric = /^[0-9]+(\.[0-9]+)?$/.test(rawHours);
  if (!isNumeric) return false;

  // 3. Convert to a float and verify 0.25 increment alignment
  const hours = parseFloat(rawHours);

  // Hours must be greater than 0, and divisible by 0.25 without a remainder
  const isValidIncrement = hours > 0 && (hours % 0.25) === 0;

  return isValidIncrement;
};

const normalizeOptionList = (response, optionType) => {
  const payload = response?.data;
  const root = payload?.data || payload;
  const nestedData = root?.data;
  const list = Array.isArray(nestedData)
    ? nestedData
    : Array.isArray(root)
      ? root
      : [];

  if (optionType === 'project_name') {
    return list.map((item) => ({
      value: String(item?.project_id || item?.id || ''),
      label: item?.project_name || item?.name || '-',
    }));
  }

  return list.map((item) => ({
    value: String(item?.activity_id || item?.id || item?.value || ''),
    label: item?.activity || item?.activity_name || item?.label || '-',
  }));
};

const parseDisplayDate = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;

  const raw = String(value).trim();
  const pattern = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/;
  const match = raw.match(pattern);

  if (match) {
    const day = Number(match[1]);
    const month = MONTH_INDEX[match[2]];
    const year = Number(match[3]);
    if (Number.isInteger(day) && Number.isInteger(month) && Number.isInteger(year)) {
      const candidate = dayjs(new Date(year, month, day));
      if (candidate.isValid()) return candidate;
    }
  }

  const fallback = dayjs(raw);
  return fallback.isValid() ? fallback : null;
};

const getCandidateValue = (rowData, candidates) => {
  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const value = candidate.includes('.')
      ? getValueByPath(rowData, candidate)
      : rowData?.[candidate];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const normalizeFormFromRow = (rowData) => {
  const rawDate = getCandidateValue(rowData, [
    'date',
    'efforts_date',
    'logDate',
    'date_of_effort',
  ]);

  const parsedDate = parseDisplayDate(rawDate);

  return {
    date: parsedDate ? parsedDate.format('DD-MMM-YYYY') : String(rawDate || ''),
    dateObj: parsedDate,
    project: String(getCandidateValue(rowData, [
      'project_id',
      'p_id',
      'do',
      'projectName',
      'project',
      'project.project_name',
      'project.projectName',
      'project_name',
      'projectNameLabel',
    ])),
    effortHours: String(getCandidateValue(rowData, [
      'effortHours',
      'efforts',
      'hours',
      'effort',
      'effort_hrs',
    ])),
    activity: String(getCandidateValue(rowData, [
      'activity_id',
      'a_id',
      'activity',
      'activity_name',
      'activityName',
    ])),
    comments: String(getCandidateValue(rowData, [
      'comments',
      'comment',
      'remark',
      'remarks',
    ])),
  };
};

const resolveRowTimeSheetId = (data) => getCandidateValue(data, [
  'timeSheetId',
  'timesheetId',
  'time_sheet_id',
  'timesheet_id',
  'id',
  'data_id',
]);

const buildEffortUpdatePayload = ({
  rowData,
  formData,
  employeeId,
  projectDropdownOptions,
  activityDropdownOptions,
}) => {
  const resolvedProjectOption = projectDropdownOptions.find(
    (option) => String(option.id) === String(formData.project) || option.name === formData.project,
  );
  const resolvedActivityOption = activityDropdownOptions.find(
    (option) => String(option.id) === String(formData.activity) || option.name === formData.activity,
  );

  const projectId = resolvedProjectOption?.id || formData.project;
  const activityId = resolvedActivityOption?.id || formData.activity;

  const dateSource = formData.dateObj?.isValid?.()
    ? formData.dateObj.format('DD-MMM-YYYY')
    : formData.date;
  const parsedDate = dayjs(dateSource, ['DD-MMM-YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'], true);
  const effortsDate = parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';

  const timeSheetId = resolveRowTimeSheetId(rowData || {});

  const isEffortValid = validateUserInput({ efforts: formData.effortHours });

  if (!isEffortValid) {
    toast.error?.('Please enter valid effort hours (positive number, increments of 0.25).');
    return null;
  }
  const effortData = {
    project_id: String(projectId || ''),
    employee_id: String(employeeId || ''),
    efforts_date: effortsDate,
    activity: String(activityId || ''),
    efforts: String(formData.effortHours || ''),
    comments: String(formData.comments || ''),
    source: Number(rowData?.source ?? 1),
    issue_status: rowData?.issue_status ?? null,
  };

  const missingFields = [];
  if (!timeSheetId) missingFields.push('timeSheetId');
  if (!effortData.project_id) missingFields.push('effortData.project_id');
  if (!effortData.efforts_date) missingFields.push('effortData.efforts_date');
  if (!effortData.activity) missingFields.push('effortData.activity');
  if (!effortData.efforts && effortData.efforts !== '0') missingFields.push('effortData.efforts');
  if (!effortData.comments) missingFields.push('effortData.comments');

  if (missingFields.length) {
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }

  return { timeSheetId, effortData };
};

const EditEffortModal = ({
  open,
  rowData,
  onClose,
  onSubmit,
  authToken,
  employeeId,
  userInfo,
  tableViewType,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [originalFormData, setOriginalFormData] = useState(DEFAULT_FORM);
  const [projectOptions, setProjectOptions] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  const [effortLoggingDates, setEffortLoggingDates] = useState(null);
  const modalRef = useRef(null);

  const minSelectableDate = effortLoggingDates?.minDate;
  const maxSelectableDate = effortLoggingDates?.maxDate;
  const isSelectableRangeSameYear = effortLoggingDates
    && minSelectableDate.getFullYear() === maxSelectableDate.getFullYear();

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;
    setEffortLoggingDates(null);

    const loadEffortLoggingDates = async () => {
      try {
        const response = await getEffortLoggingDates({ authToken });
        if (!isActive || !response || response.status >= 400
          || response.success === false || response.data?.success === false) return;

        const payload = response?.data?.data;
        const configuration = Array.isArray(payload) ? payload[0] : payload;
        const minDate = dayjs(configuration?.start_date, 'YYYY-MM-DD', true);
        const maxDate = dayjs(configuration?.end_date, 'YYYY-MM-DD', true);
        if (!minDate.isValid() || !maxDate.isValid() || minDate.isAfter(maxDate, 'day')) return;

        setEffortLoggingDates({ minDate: minDate.toDate(), maxDate: maxDate.toDate() });
      } catch (error) {
        if (isActive) setEffortLoggingDates(null);
      }
    };

    loadEffortLoggingDates();

    return () => {
      isActive = false;
    };
  }, [open, authToken]);

  const projectDropdownOptions = useMemo(
    () => projectOptions.map((option) => ({ id: option.value, name: option.label })),
    [projectOptions],
  );

  const activityDropdownOptions = useMemo(
    () => activityOptions.map((option) => ({ id: option.value, name: option.label })),
    [activityOptions],
  );

  const selectedProjectOption = useMemo(
    () => projectDropdownOptions.find(
      (option) => String(option.id) === String(formData.project) || option.name === formData.project,
    ) || null,
    [projectDropdownOptions, formData.project],
  );

  const selectedActivityOption = useMemo(
    () => activityDropdownOptions.find(
      (option) => String(option.id) === String(formData.activity) || option.name === formData.activity,
    ) || null,
    [activityDropdownOptions, formData.activity],
  );

  const hasFormChanged = useMemo(() => {
    return (
      formData.date !== originalFormData.date
      || formData.project !== originalFormData.project
      || formData.effortHours !== originalFormData.effortHours
      || formData.activity !== originalFormData.activity
      || formData.comments !== originalFormData.comments
    );
  }, [formData, originalFormData]);

  const loadDropdownOptions = useCallback(async () => {
    try {
      const [projectResponse, activityResponse] = await Promise.all([
        fetchProjectOptions({ authToken, employeeId }),
        fetchActivityOptions({ authToken, employeeId }),
      ]);

      setProjectOptions(normalizeOptionList(projectResponse, 'project_name'));
      setActivityOptions(normalizeOptionList(activityResponse, 'activities'));
    } catch (error) {
      setProjectOptions([]);
      setActivityOptions([]);
    }
  }, [authToken, employeeId]);

  useEffect(() => {
    if (!open) return;
    const normalized = normalizeFormFromRow(rowData || {});
    setFormData(normalized);
    setOriginalFormData(normalized);
  }, [open, rowData]);

  useEffect(() => {
    if (!open) return;
    loadDropdownOptions();
  }, [open, loadDropdownOptions]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const validateForm = (nextFormData) => {
    if (!nextFormData.dateObj?.isValid?.()) {
      toast.error('Please select a valid date.');
      return false;
    }

    if (!String(nextFormData.project || '').trim()) {
      toast.error('Please select a project.');
      return false;
    }

    if (!String(nextFormData.activity || '').trim()) {
      toast.error('Please select an activity.');
      return false;
    }

    if (!String(nextFormData.effortHours || '').trim()) {
      toast.error('Please enter effort hours.');
      return false;
    }

    if (!validateUserInput({ efforts: nextFormData.effortHours })) {
      toast.error('Please enter valid effort hours (increments of 0.25).');
      return false;
    }

    if (!String(nextFormData.comments || '').trim()) {
      toast.error('Please enter comments.');
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!open || isSubmitConfirmOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // 1. Existing checks for standard dropdowns
        const isDropdownClick = event.target.closest('.edit-effort-search-dropdown')
      || event.target.closest('.dropdown-menu')
      || event.target.closest('[class*="dropdown"]')
      || event.target.closest('[class*="popup"]')
      || event.target.closest('.p-dropdown-panel');

        // 2. NEW: Prevent closing if clicking inside any MUI Dropdown / Menu / Listbox / Popover
        const isMuiClick = event.target.closest('.MuiPopover-root')
      || event.target.closest('.MuiMenu-root')
      || event.target.closest('[role="listbox"]')
      || event.target.closest('.MuiAutocomplete-popper');

        if (!isDropdownClick && !isMuiClick) {
          onClose?.();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [open, isSubmitConfirmOpen, onClose]);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleProjectChange = (event) => {
    const selected = event?.target?.value;
    updateField('project', selected?.id || selected?.name || '');
  };

  const handleActivityChange = (event) => {
    const selected = event?.target?.value;
    updateField('activity', selected?.id || selected?.name || '');
  };

  const handleEffortChange = (event) => {
    const value = event.target.value;

    // Allow empty input so users can backspace/clear the field
    if (value === '') {
      updateField('effortHours', '');
      return;
    }

    const numericValue = parseFloat(value);

    // Only update state if within 0 - 24 range
    if (numericValue >= 0 && numericValue <= 24) {
      updateField('effortHours', value);
    }
  };

  const handleUpdate = async () => {
    // Bulletproof extractor tailored exactly to your API's wrapper object
    const extractServerMessage = (res) => {
      if (!res) return 'Failed to update effort.';

      // 1. Look down the exact path from your console log: res.data.response.data.message
      const exactMessage
        = res?.data?.response?.data?.message // Wrapped Axios Error
        || res?.response?.data?.message; // Standard Axios Error

      if (exactMessage && typeof exactMessage === 'string') {
        return exactMessage;
      }

      // 2. Check direct response data, but ONLY if it's not the generic Axios object
      if (res?.data?.message && typeof res.data.message === 'string' && !res.data.isAxiosError) {
        return res.data.message;
      }

      // 3. Fallback for custom backend 'error' keys
      const alternativeError = res?.data?.response?.data?.error || res?.response?.data?.error;
      if (alternativeError && typeof alternativeError === 'string') {
        return alternativeError;
      }

      return 'Failed to update effort.';
    };

    try {
      const payload = buildEffortUpdatePayload({
        rowData,
        formData,
        employeeId,
        projectDropdownOptions,
        activityDropdownOptions,
      });

      if (!validateForm(formData)) {
        return;
      }

      if (!payload) {
        return;
      }

      const { timeSheetId, effortData } = payload;

      const response = await updateLoggedEffort({
        authToken,
        timeSheetId,
        effortData,
        userInfo,
        tableViewType: tableViewType || 'list_view',
      });

      // Check strict success conditions
      const isSuccess = response?.status && response.status < 400
       && response?.data?.success !== false && response?.success !== false;

      if (isSuccess) {
        const successMessage = response?.data?.message || response?.message || 'Effort updated successfully.';
        toast.success?.(successMessage);
        onSubmit?.({ timeSheetId, effortData });
        setIsSubmitConfirmOpen(false);
        onClose?.();
      } else {
        toast.error?.(extractServerMessage(response));
      }
    } catch (error) {
      // The error object matches the { data: AxiosError } structure here
      toast.error?.(extractServerMessage(error));
    }
  };

  if (!open) return null;

  return (
    <div className="edit-effort-modal-backdrop" role="presentation">
      <div className="edit-effort-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Edit Effort">
        <div className="edit-effort-modal-header">
          <h3>Edit Effort</h3>
        </div>

        <div className="edit-effort-form-grid">
          <div
            className="edit-effort-date-field dri-grid-placement"
            style={{ position: 'relative' }}
          >
            {effortLoggingDates && <DateRangeInput
              label="Date"
              showFromTo={false}
              placeholder="Select Date"
              isClearable
              isRequired
              minYear={minSelectableDate.getFullYear()}
              maxYear={maxSelectableDate.getFullYear()}
              minDay={minSelectableDate}
              maxDay={maxSelectableDate}
              minMonth={isSelectableRangeSameYear ? minSelectableDate.getMonth() + 1 : undefined}
              maxMonth={isSelectableRangeSameYear ? maxSelectableDate.getMonth() + 1 : undefined}
              fromValue={formData.dateObj}
              onApply={(date) => {
                updateField('date', date ? date.format('DD-MMM-YYYY') : '');
                updateField('dateObj', date || null);
              }}
              onReset={() => {
                updateField('date', '');
                updateField('dateObj', null);
              }}
            />}
          </div>

          <label>
            <span className="required-label">Project Name<span className="asteric">*</span></span>
            <SearchDropdown
              label=""
              options={projectDropdownOptions}
              placeholder="Select Project"
              value={selectedProjectOption || formData.project}
              onChange={handleProjectChange}
              dropdownCss="edit-effort-search-dropdown"
              searchBox
            />
          </label>

          <label>
            <span className="effort-label-row">
              <span className="required-label">Efforts (Hrs)<span className="asteric">*</span></span>
              <span className="effort-info-wrapper">
                <button
                  type="button"
                  className="effort-info-btn"
                  aria-label="Effort conversion information"
                >i</button>
                <span className="effort-info-tooltip" role="tooltip">
                  <span>1 hr = 1</span>
                  <span>1 hr 15 min = 1.25</span>
                  <span>1 hr 30 min = 1.5</span>
                  <span>1 hr 45 min = 1.75</span>
                </span>
              </span>
            </span>
            <input
              type="number"
              step="0.25"
              value={formData.effortHours}
              placeholder="0.00"
              max={24}
              onChange={handleEffortChange}
            />
          </label>

          <label>
            <span className="required-label">Activity<span className="asteric">*</span></span>
            <SearchDropdown
              label=""
              options={activityDropdownOptions}
              placeholder="Select Activity"
              value={selectedActivityOption || formData.activity}
              onChange={handleActivityChange}
              dropdownCss="edit-effort-search-dropdown"
              searchBox
            />
          </label>

          <label className="full-width">
            <span className="required-label">Comments<span className="asteric">*</span></span>
            <input
              type="text"
              value={formData.comments}
              placeholder="Enter comments"
              onChange={(event) => updateField('comments', event.target.value)}
            />
          </label>
        </div>

        <div className="edit-effort-modal-footer">
          <button type="button" className="edit-effort-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="edit-effort-update"
            onClick={() => {
              if (validateForm(formData)) {
                setIsSubmitConfirmOpen(true);
              }
            }}
            disabled={!hasFormChanged}
          >Update</button>
        </div>
      </div>

      <ActionConfirmModal
        open={isSubmitConfirmOpen}
        currentAction="Update"
        title="Update Effort"
        message="Do you want to submit the updated effort details?"
        confirmText="Submit"
        cancelText="Cancel"
        onCancel={() => setIsSubmitConfirmOpen(false)}
        onConfirm={handleUpdate}
      />
    </div>
  );
};

EditEffortModal.propTypes = {
  open: PropTypes.bool,
  rowData: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  authToken: PropTypes.string,
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userInfo: PropTypes.object,
  tableViewType: PropTypes.string,
};

EditEffortModal.defaultProps = {
  open: false,
  rowData: null,
  onClose: null,
  onSubmit: null,
  authToken: '',
  employeeId: '',
  tableViewType: 'list_view',
};

export default EditEffortModal;
